import { computed, observable } from 'mobx'
import { once } from 'lodash-es'
import { of, type Observable } from 'rxjs'
import { type Membership } from '@/features/iam/workspaces/Membership'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import { generateAvatarFallback } from '@/utils/image-util'
import { asAtom } from '@/utils/mobx-util'
import type { UserDataType } from './UserData'

export type SearchableUserProps = ModelProps<SearchableUser>

export class SearchableUser extends Model<SearchableUser> {
  @observable accessor userId = Id.none
  @observable accessor publicId = Id.none
  @observable accessor givenName = ''
  @observable accessor familyName = ''
  @observable accessor email = ''
  @observable accessor phone = ''
  @observable accessor picture = ''
  @observable accessor userDataType: UserDataType = 'User'
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor lastSeen: Date | null = null
  @observable accessor memberships: Membership[] = []
  @observable accessor isWorkspaceCustomer = false

  /**
   * The use of `asAtom` ensures the online status is only synced while there
   * are observers on {@link online}.
   */
  private readonly _online

  static get none() {
    return lazyNone()
  }

  constructor(online$: Observable<boolean>) {
    super()
    this._online = asAtom(online$, false)
  }

  /**
   * Whether the user is currently online.
   */
  @computed
  get online() {
    return this._online()
  }

  @computed
  get avatar() {
    return (
      this.picture || generateAvatarFallback(this.displayName || this.email)
    )
  }

  @computed
  get displayName() {
    return `${this.givenName} ${this.familyName}`
  }
}

const lazyNone = once(() =>
  new SearchableUser(of(false)).set({
    givenName: '',
    familyName: '',
    email: '',
  }),
)
